import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Grid,
  Typography,
  TextField,
  Card,
  CardContent,
  Box,
  Chip,
  Stack,
  Skeleton,
  Button,
  InputAdornment,
  Paper,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  School as SchoolIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useApiGet } from '../../utils/httpClientTs';
import { getTrainingList } from '../../endpoints/training';
import TrainingCategorySelector from '../../Components/TrainingCategorySelector';
import TrainingTopicSelector from '../../Components/TrainingTopicSelector';
import { Training as TrainingType } from '../../types/training';
import trainingLogo from '../../assets/DefaultIcon.png';
import AlertItem from '../../Components/AlertItem';

interface TrainingCardProps {
  training: TrainingType;
  onClick?: (training: TrainingType) => void;
}

const TrainingCard: React.FC<TrainingCardProps> = ({ training, onClick }) => {
  const navigate = useNavigate();
  const image = training.icon_image_url || trainingLogo;

  const handleClick = () => {
    if (onClick) {
      onClick(training);
    } else {
      navigate(`/training/${training.id}`);
    }
  };

  return (
    <Card
      onClick={handleClick}
      sx={{
        display: 'flex',
        padding: 1,
        borderRadius: '12px',
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
        },
        height: '100px',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          width: 80,
          height: 80,
          flexShrink: 0,
          backgroundColor: 'grey.200',
          borderRadius: '8px',
          boxShadow: '0 2px 8px 0 #c1c9d7, 0 -2px 8px 0 #cce1e9',
          backgroundImage: `url(${image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      />
      <CardContent
        sx={{
          pl: 1.5,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          py: 0.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 0.5,
          }}
        >
          <Typography
            variant="subtitle1"
            component="div"
            sx={{ fontWeight: 600, color: 'text.primary', fontSize: '1rem' }}
          >
            {training.name}
          </Typography>
          {training.user_completed && (
            <Chip
              icon={<CheckCircleIcon />}
              label="Completed"
              color="success"
              size="small"
              sx={{ ml: 1 }}
            />
          )}
        </Box>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 0.5,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            fontSize: '0.875rem',
          }}
        >
          {training.description}
        </Typography>

        <Stack direction="row" spacing={1} flexWrap="wrap">
          {training.categories?.slice(0, 2).map((category, index) => (
            <Chip
              key={index}
              label={
                typeof category === 'object' && category && 'name' in category
                  ? (category as { id: string; name: string }).name
                  : String(category)
              }
              size="small"
              variant="outlined"
              color="primary"
            />
          ))}
          {training.categories && training.categories.length > 2 && (
            <Chip
              label={`+${training.categories.length - 2}`}
              size="small"
              variant="outlined"
              color="default"
            />
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

const TrainingSkeleton: React.FC = () => (
  <Card
    sx={{
      display: 'flex',
      padding: 1,
      borderRadius: '12px',
      height: '100px',
      alignItems: 'center',
      mb: 1,
    }}
  >
    <Skeleton
      variant="rectangular"
      width={80}
      height={80}
      sx={{
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    />
    <CardContent sx={{ pl: 1.5, flex: 1, py: 0.5 }}>
      <Skeleton variant="text" height={24} width="60%" />
      <Skeleton variant="text" height={16} width="100%" />
      <Skeleton variant="text" height={16} width="80%" />
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Skeleton variant="rectangular" height={20} width={60} />
        <Skeleton variant="rectangular" height={20} width={80} />
      </Box>
    </CardContent>
  </Card>
);

const TrainingPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const updateSearchParams = useCallback(
    (key: string, value: string | string[]) => {
      if (
        !value ||
        value === '' ||
        (Array.isArray(value) && value.length === 0)
      ) {
        searchParams.delete(key);
      } else {
        const paramValue = Array.isArray(value) ? value.join(',') : value;
        searchParams.set(key, paramValue);
      }
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  const [categories, setCategories] = useState<any[]>([]);
  const [topics, setTopics] = useState<any[]>(
    searchParams.get('topics') ? [searchParams.get('topics')!] : []
  );
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [delaySearch, setDelaySearch] = useState(
    searchParams.get('search') || ''
  );
  const [showFilters, setShowFilters] = useState(false);

  // Update search params when search changes
  useEffect(() => {
    updateSearchParams('search', search);
  }, [search, updateSearchParams]);

  // Update params when categories changes
  useEffect(() => {
    updateSearchParams('categories', categories);
  }, [categories, updateSearchParams]);

  // Update params when topics changes
  useEffect(() => {
    updateSearchParams('topics', topics);
  }, [topics, updateSearchParams]);

  // Debounce search input
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearch(delaySearch);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [delaySearch]);

  const { data, isLoading, error } = useApiGet(
    ['training-list', search, categories, topics],
    () => getTrainingList({ search, categories, topics }),
    {
      enabled: true,
      retry: 1,
    }
  );

  // Handle both array and object response formats
  const trainingData = Array.isArray(data) ? data : data?.data || [];
  const canManage = !Array.isArray(data) && data?.can_manage === true;

  const handleCreateTraining = () => {
    navigate('/training/create');
  };

  const clearFilters = () => {
    setSearch('');
    setDelaySearch('');
    setCategories([]);
    setTopics([]);
  };

  const hasActiveFilters = search || categories.length > 0 || topics.length > 0;

  // Helper function to get category display names
  const getCategoryDisplayName = (category: any) => {
    return typeof category === 'string' ? category : category?.name || category;
  };

  // Helper function to get topic display names
  const getTopicDisplayName = (topic: any) => {
    return typeof topic === 'string' ? topic : topic?.name || topic;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        {/* Simple Header with Create Button */}
        {canManage && (
          <Grid item xs={12}>
            <Box sx={{ textAlign: 'right' }}>
              <Button
                variant="contained"
                startIcon={<SchoolIcon />}
                onClick={handleCreateTraining}
                size="small"
                color="success"
              >
                Create
              </Button>
            </Box>
          </Grid>
        )}

        {/* Search and Basic Filters */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  label="Search Training"
                  id="search-field"
                  size="small"
                  value={delaySearch}
                  fullWidth
                  onChange={(event) => setDelaySearch(event.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant={showFilters ? 'contained' : 'outlined'}
                  startIcon={<FilterIcon />}
                  onClick={() => setShowFilters(!showFilters)}
                >
                  Filters
                </Button>
              </Grid>

              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={clearFilters}
                  disabled={!hasActiveFilters}
                >
                  Clear
                </Button>
              </Grid>
            </Grid>

            {/* Advanced Filters */}
            {showFilters && (
              <Box sx={{ mt: 2 }}>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TrainingTopicSelector
                      value={topics}
                      handleValueChange={(value) => setTopics(value)}
                      label="Training Topic"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TrainingCategorySelector
                      value={categories}
                      handleValueChange={(value) => setCategories(value)}
                      label="Training Category"
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
          </Paper>

          {/* Active Filters Display */}
          {hasActiveFilters && (
            <Paper sx={{ p: 2, mb: 3, backgroundColor: 'action.hover' }}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, fontWeight: 'bold' }}
              >
                Active Filters:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {search && (
                  <Chip
                    label={`Search: "${search}"`}
                    onDelete={() => {
                      setSearch('');
                      setDelaySearch('');
                    }}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                )}
                {categories.map((category, index) => (
                  <Chip
                    key={index}
                    label={`Category: ${getCategoryDisplayName(category)}`}
                    onDelete={() =>
                      setCategories(categories.filter((_, i) => i !== index))
                    }
                    color="secondary"
                    variant="outlined"
                    size="small"
                  />
                ))}
                {topics.map((topic, index) => (
                  <Chip
                    key={index}
                    label={`Topic: ${getTopicDisplayName(topic)}`}
                    onDelete={() =>
                      setTopics(topics.filter((_, i) => i !== index))
                    }
                    color="secondary"
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Stack>
            </Paper>
          )}
        </Grid>

        {/* Results */}
        <Grid item xs={12}>
          {error && (
            <Box sx={{ mb: 3 }}>
              <AlertItem
                severity="error"
                description="Error loading training data. Please try again."
                onClose={() => {}}
                keepOpen={true}
              />
            </Box>
          )}

          {isLoading ? (
            <Grid container spacing={2}>
              {[...Array(6)].map((_, index) => (
                <Grid item xs={12} key={index} sx={{ mb: 2 }}>
                  <TrainingSkeleton />
                </Grid>
              ))}
            </Grid>
          ) : trainingData && trainingData.length > 0 ? (
            <Grid container spacing={2}>
              {trainingData.map((training) => (
                <Grid item xs={12} key={training.id}>
                  <TrainingCard training={training} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Card sx={{ p: 4, textAlign: 'center' }}>
              <SchoolIcon
                sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }}
              />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No training found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your search criteria or create a new training
                program.
              </Typography>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default TrainingPage;
